/**
 * Auth-Gateway ist der TaxPilot Platform Hub (platform.kalkuel.app).
 *
 * Architektur (1:1 von InvoiceAI uebernommen — siehe
 * ../../invoiceai/frontend/src/lib/auth.ts und PLATFORM-INTEGRATION-GUIDE.md):
 *
 * - Der Hub uebernimmt Login (Keycloak SSO) UND Workspace-Onboarding.
 * - PayPilot redirected zum Hub, der Hub redirected zurück mit `?workspace_id=...`.
 * - Unser lokaler OIDC-Flow läuft als Fallback, wenn der User direkt mit einem
 *   gültigen Token hereinkommt (Silent-Renew, Back-Button, etc.).
 *
 * Token flow intern: Authorization Code + PKCE
 * Tokens bleiben im Memory (kein localStorage) → XSS-Schutz.
 */
import {
  UserManager,
  InMemoryWebStorage,
  WebStorageStateStore,
  type User as OidcUser,
} from "oidc-client-ts";

export const PLATFORM_HUB_URL =
  import.meta.env.VITE_PLATFORM_HUB_URL ?? "https://platform.kalkuel.app";

const KEYCLOAK_AUTHORITY =
  import.meta.env.VITE_KEYCLOAK_AUTHORITY ??
  "https://keycloak.kalkuel.app/realms/taxpilot";
const CLIENT_ID =
  import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? "taxpilot-paypilot";

export const userManager = new UserManager({
  authority: KEYCLOAK_AUTHORITY,
  client_id: CLIENT_ID,
  redirect_uri: window.location.origin + "/auth/callback",
  post_logout_redirect_uri: window.location.origin,
  response_type: "code",
  scope: "openid profile email",
  // automaticSilentRenew via iframe wird von Keycloak's
  // frame-ancestors 'self' CSP blockiert (Web-Origins-Setting im
  // Keycloak-Client würde das fixen, aber wir brauchen das nicht
  // unbedingt). Bei Token-Ablauf macht der User einen neuen vollen
  // Login-Roundtrip — Keycloak lässt ihn dank SSO-Session ohne
  // Passwort durch, nur ein zusätzlicher Redirect.
  automaticSilentRenew: false,
  // PKCE code_verifier needs to survive the redirect → sessionStorage
  stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
  // Tokens stay in memory only — never persisted to storage (XSS protection)
  userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
});

/**
 * Redirect zum Platform Hub mit return_url auf die aktuelle Seite.
 *
 * Der Hub kuemmert sich um Keycloak-Login und Workspace-Onboarding und
 * schickt den User mit `?workspace_id=...` zurück.
 */
export function redirectToHub(returnUrl?: string): void {
  const url = returnUrl ?? window.location.href;
  window.location.href =
    PLATFORM_HUB_URL + "?return_url=" + encodeURIComponent(url);
}

/** Flag, den AuthContext waehrend eines laufenden Silent-Signin setzt. */
export const SILENT_SIGNIN_PENDING_FLAG = "paypilot_silent_signin_pending";

/**
 * Get current access token, or null if not authenticated.
 *
 * Wenn gerade ein Silent-Signin nach Hub-Rückkehr läuft (Flag in
 * sessionStorage), warten wir bis zu 3 Sekunden auf den Token — sonst
 * laufen die ersten Queries beim F5 in ein 401-Race.
 */
export async function getAccessToken(): Promise<string | null> {
  try {
    let user = await userManager.getUser();
    if (user && !user.expired) return user.access_token;

    if (sessionStorage.getItem(SILENT_SIGNIN_PENDING_FLAG) === "true") {
      const deadline = Date.now() + 3000;
      while (Date.now() < deadline) {
        if (sessionStorage.getItem(SILENT_SIGNIN_PENDING_FLAG) !== "true") break;
        await new Promise((r) => setTimeout(r, 100));
        user = await userManager.getUser();
        if (user && !user.expired) return user.access_token;
      }
      user = await userManager.getUser();
    }

    return user && !user.expired ? user.access_token : null;
  } catch {
    return null;
  }
}

/** Parse JWT payload without verification (for reading claims client-side).
 *
 * Wichtig: `atob()` dekodiert Base64 als Latin-1 (jeder Byte wird zum
 * Code-Point). Wenn das JWT UTF-8-Bytes für Umlaute enthält
 * (z. B. `c3bc` für „ü"), würden wir „ü" bekommen. Deshalb erst die
 * Bytes herausziehen und dann mit TextDecoder als UTF-8 dekodieren.
 */
export function parseJwtPayload(token: string): Record<string, unknown> {
  try {
    const base64 = token.split(".")[1];
    const binary = atob(base64.replace(/-/g, "+").replace(/_/g, "/"));
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const json = new TextDecoder("utf-8").decode(bytes);
    return JSON.parse(json);
  } catch {
    return {};
  }
}

/** TaxPilot-specific claims from the JWT (Keycloak Custom-Mapper). */
export interface TaxPilotClaims {
  user_id: string;
  memberships: TaxPilotMembership[];
}

export interface TaxPilotMembership {
  workspace_id: string;
  workspace_handle: string;
  workspace_display_name: string;
  membership_id: string;
  role: "OWNER" | "ADMIN" | "ACCOUNTANT";
}

/** Extract taxpilot claims from access token. */
export function getTaxPilotClaims(token: string): TaxPilotClaims | null {
  const payload = parseJwtPayload(token);
  const tp = payload.taxpilot as TaxPilotClaims | undefined;
  if (!tp?.user_id) return null;
  return tp;
}

export type { OidcUser };
